// =====================================================================
//  PANEL DE CONTROL
//
//  Réplica de vPanel() del prototipo, leyendo de Postgres. Lo que se
//  agrega es la advertencia de garantía por lote del art. 148, que el
//  prototipo no podía mostrar porque calculaba la garantía sumando solo
//  las concesiones marcadas (PLAN §5.2).
// =====================================================================

import { Link } from 'react-router-dom'
import {
  useAlertas,
  useCausa,
  useConcesiones,
  usePresupuesto,
  usePuntajes,
} from '../datos/consultas'
import { Alerta, Cargando, Cuenta, ErrorCarga, Kpi, Puntaje, Tag, TituloCard } from '../componentes/ui'
import { Pagina } from '../App'
import { diasHasta, fecha, fmt, num, plural } from '../dominio/formato'
import { ESTADOS, INTERES } from '../tipos/dominio'

export function Panel() {
  const causa = useCausa()
  const concesiones = useConcesiones()
  const puntajes = usePuntajes()
  const presupuesto = usePresupuesto()
  const alertas = useAlertas()

  if (concesiones.isError) return <ErrorCarga error={concesiones.error} />
  if (concesiones.isLoading || !concesiones.data || !causa.data) return <Cargando />

  const todas = concesiones.data
  const c = causa.data
  const p = presupuesto.data

  const enRemate = todas.filter((x) => x.estado === 'EN_REMATE')
  const retiradas = todas.filter((x) => x.estado === 'RETIRADA')
  const marcadas = enRemate.filter((x) => x.interes === 'ALTA' || x.interes === 'MEDIA')

  const deudaTotal = todas.reduce((s, x) => s + num(x.monto_adeudado), 0)
  const deudaActiva = enRemate.reduce((s, x) => s + num(x.monto_adeudado), 0)

  const comprometido = num(p?.comprometido)
  const monto = p?.presupuesto === null || p?.presupuesto === undefined ? null : num(p.presupuesto)
  const garantiaLotes = num(p?.garantia_lotes)
  const garantiaSoloMarcadas = num(p?.garantia_solo_marcadas)
  const brechaGarantia = garantiaLotes - garantiaSoloMarcadas

  const diasRemate = diasHasta(c.fecha_remate)
  // Límite legal de publicación: 30 días corridos antes del remate, sin
  // contar el día de publicación ni el del remate (art. 147).
  const limitePublicacion = c.fecha_remate
    ? new Date(new Date(c.fecha_remate).getTime() - 31 * 86_400_000)
    : null
  const diasPublicacion = limitePublicacion ? diasHasta(limitePublicacion.toISOString()) : 0

  const puntajePor = new Map(puntajes.data?.map((x) => [x.concesion_id, x.puntaje]) ?? [])
  const mejores = [...enRemate]
    .sort(
      (a, b) =>
        (puntajePor.get(b.id) ?? 0) - (puntajePor.get(a.id) ?? 0) ||
        num(b.monto_adeudado) - num(a.monto_adeudado),
    )
    .slice(0, 6)

  const enTramite = alertas.data?.filter((a) => a.alerta === 'EN_TRAMITE').length ?? 0
  const deudaCero = alertas.data?.filter((a) => a.alerta === 'DEUDA_CERO') ?? []
  const posiblesLotes = alertas.data?.filter((a) => a.alerta === 'POSIBLE_LOTE').length ?? 0

  return (
    <>
      <Pagina
        titulo="Panel de control"
        sub={
          <>
            Causa {c.rol} · {c.tribunal} · Remate fijado para el {fecha(c.fecha_remate)} a las 12:00 h
            por {c.modalidad === 'VIDEOCONFERENCIA' ? 'Zoom' : 'audiencia presencial'}
          </>
        }
      />

      {diasRemate > 0 ? (
        <Alerta tono="info">
          <b>Faltan {diasRemate} días para el remate.</b> La resolución de folio 8 (23/07/2026) ordenó
          publicar en el Diario Oficial con un mínimo de 30 días corridos de antelación
          {c.fecha_publicacion ? (
            <>
              . Publicada el {fecha(c.fecha_publicacion)}.
            </>
          ) : diasPublicacion > 0 ? (
            <>
              , es decir a más tardar el {fecha(limitePublicacion?.toISOString())} ({diasPublicacion}{' '}
              días). Sin esa publicación el remate no puede realizarse.
            </>
          ) : (
            <>
              . El plazo de publicación ya venció y no hay fecha registrada: verifica el Diario
              Oficial.
            </>
          )}
        </Alerta>
      ) : (
        <Alerta tono="warn">
          <b>La fecha de remate ya pasó.</b> Actualiza los resultados de adjudicación en cada ficha.
        </Alerta>
      )}

      {monto !== null && comprometido > monto ? (
        <Alerta tono="bad">
          <b>Presupuesto excedido.</b> Las posturas de las {marcadas.length} concesiones marcadas
          suman {fmt(comprometido)} frente a un presupuesto de {fmt(monto)}. Diferencia:{' '}
          {fmt(comprometido - monto)}.
        </Alerta>
      ) : null}

      {monto === null ? (
        <Alerta tono="warn">
          <b>No hay presupuesto fijado.</b> Sin una cifra de referencia, el criterio «calce con el
          presupuesto» del puntaje toma su valor más bajo para todas. Defínelo en{' '}
          <Link to="/presupuesto">Presupuesto y posturas</Link>.
        </Alerta>
      ) : null}

      {/* La advertencia que el prototipo no podía dar: art. 148, la boleta
          cubre el lote completo, no solo la mina que interesa. */}
      {brechaGarantia > 0 ? (
        <Alerta tono="warn">
          <b>La garantía del art. 148 es mayor que lo marcado.</b> Las concesiones marcadas están
          amarradas a lotes que incluyen otras que no están marcadas, y la boleta de depósito cubre el
          total adeudado de <b>todo el lote</b>. Hay que rendir {fmt(garantiaLotes)}, no{' '}
          {fmt(garantiaSoloMarcadas)}: son {fmt(brechaGarantia)} más de caja disponible el día del
          remate. Detalle en <Link to="/lotes">Lotes de subasta</Link>.
        </Alerta>
      ) : null}

      {deudaCero.length > 0 ? (
        <Alerta tono="warn">
          <b>{deudaCero[0].nombre}</b> figura en la nómina con $0 adeudados. Con mínimo cero no hay
          aritmética de remate posible: solicita certificado de deuda a la TGR antes de considerarla.
        </Alerta>
      ) : null}

      <Cuenta
        datos={[
          [Math.max(diasRemate, 0), 'días al remate'],
          [enRemate.length, 'aún en remate'],
          [retiradas.length, 'retiradas por pago'],
          [marcadas.length, 'marcadas'],
        ]}
      />

      <div className="grid kpis" style={{ marginBottom: 18 }}>
        <Kpi
          etiqueta="Deuda total del listado"
          valor={fmt(deudaTotal)}
          pie={`${todas.length} concesiones en ${new Set(todas.map((x) => x.comuna)).size} comunas`}
        />
        <Kpi
          etiqueta="Mínimo vigente en remate"
          valor={fmt(deudaActiva)}
          pie={`Suma de mínimos de las ${enRemate.length} activas (art. 149)`}
        />
        <Kpi
          etiqueta="Comprometido en posturas"
          valor={fmt(comprometido)}
          color={monto !== null && comprometido > monto ? 'var(--bad)' : undefined}
          pie={
            monto
              ? `${Math.round((comprometido / monto) * 100)}% del presupuesto de ${fmt(monto)}`
              : 'sin presupuesto fijado'
          }
        />
        <Kpi
          etiqueta="Garantía a rendir"
          valor={fmt(garantiaLotes)}
          color={brechaGarantia > 0 ? 'var(--warn)' : undefined}
          pie="Boleta por el total adeudado del lote (art. 148)"
        />
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <TituloCard>Mejor evaluadas · aún en remate</TituloCard>
        <table>
          <thead>
            <tr>
              <th>Concesión</th>
              <th>Comuna</th>
              <th className="num">Mínimo</th>
              <th className="num">Retiro dueño</th>
              <th>Estado</th>
              <th>Interés</th>
              <th style={{ width: 120 }}>Puntaje</th>
            </tr>
          </thead>
          <tbody>
            {mejores.map((x) => (
              <tr key={x.id}>
                <td>
                  <Link
                    to={`/listado?ficha=${x.rol_nacional}`}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    <div className="nm">{x.nombre}</div>
                    <div className="sub">{x.titular}</div>
                  </Link>
                </td>
                <td className="sub">{x.comuna}</td>
                <td className="num">{fmt(x.minimo_subasta)}</td>
                <td className="num" style={{ color: 'var(--txt3)' }}>
                  {fmt(x.costo_retiro_dueno)}
                </td>
                <td>
                  <Tag clase={ESTADOS[x.estado].c}>{ESTADOS[x.estado].t}</Tag>
                </td>
                <td>
                  <Tag clase={INTERES[x.interes].c}>{INTERES[x.interes].t}</Tag>
                </td>
                <td>
                  <Puntaje valor={puntajePor.get(x.id) ?? 0} ancho="100%" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))' }}>
        <div className="card" style={{ background: 'var(--panel2)' }}>
          <TituloCard>Riesgo del listado</TituloCard>
          <p style={{ fontSize: 12.5, color: 'var(--txt2)' }}>
            <b style={{ color: 'var(--txt)' }}>{plural(enTramite, 'concesión', 'concesiones')} en
            trámite</b>{' '}
            (tipo T) por{' '}
            {fmt(
              todas.filter((x) => x.rol_tipo === 'T').reduce((s, x) => s + num(x.monto_adeudado), 0),
            )}
            . En ellas no se adquiere una concesión constituida sino una situación jurídica todavía
            en formación.
          </p>
        </div>
        <div className="card" style={{ background: 'var(--panel2)' }}>
          <TituloCard>Lotes por confirmar</TituloCard>
          <p style={{ fontSize: 12.5, color: 'var(--txt2)' }}>
            {posiblesLotes > 0 ? (
              <>
                <b style={{ color: 'var(--txt)' }}>{posiblesLotes} concesiones</b> comparten número
                de rol con otra de su misma comuna. Si el art. 148 se resuelve por esa columna, ya
                vienen amarradas. Se sabrá con la publicación en el Diario Oficial.
              </>
            ) : (
              'Ninguna concesión comparte número de rol dentro de su comuna.'
            )}
          </p>
        </div>
        <div className="card" style={{ background: 'var(--panel2)' }}>
          <TituloCard>Último día para retirar</TituloCard>
          <p style={{ fontSize: 12.5, color: 'var(--txt2)' }}>
            Los titulares pueden sacar sus concesiones del remate hasta el momento mismo del remate,
            pagando el doble de lo adeudado (art. 149 inc. 2). Hay que{' '}
            <b style={{ color: 'var(--txt)' }}>verificar el listado 48 h antes</b> — es el ítem 10
            del due diligence.
          </p>
        </div>
      </div>
    </>
  )
}
